import {
  useState,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { NEWS_ITEMS, formatDate } from "../data/news";
import "./Hero.css";

const PREVIEW_COUNT = 2;

const SUBTITLES = [
  {
    type: "plain",
    text: "Courses on applied mathematics, machine learning, optimization, and beyond. Free for everyone.",
  },
  {
    type: "quote",
    text: "“The time will come when these things which are now hidden from you will be brought into the light.”",
  },
];

/**
 * Hero section with search, recent news feed, and alternating subtitle.
 * @param {{ onSearch: (q: string) => void }} props
 */
const Hero = forwardRef(function Hero({ onSearch, allTags }, ref) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [subtitleIdx, setSubtitleIdx] = useState(0);
  const [isSmall, setIsSmall] = useState(
    () => window.matchMedia("(max-width: 479px)").matches,
  );
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const modalRef = useRef(null);

  const sortedTags = useMemo(
    () => [...allTags].sort((a, b) => a.localeCompare(b)),
    [allTags],
  );

  const suggestions = useMemo(() => {
    if (!isOpen) return [];
    return query.trim().length > 0
      ? sortedTags.filter((t) => t.toLowerCase().includes(query.toLowerCase()))
      : sortedTags;
  }, [isOpen, query, sortedTags]);

  const scrollToResults = () =>
    setTimeout(() => {
      document
        .getElementById("search-results")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIdx(-1);
    setIsOpen(true);
  };

  const selectTag = (tag) => {
    setQuery(tag);
    setIsOpen(false);
    setActiveIdx(-1);
    onSearch(tag);
    scrollToResults();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0) {
        selectTag(suggestions[activeIdx]);
      } else if (suggestions.length > 0) {
        selectTag(suggestions[0]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIdx(-1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      if (suggestions.length > 0) selectTag(suggestions[0]);
      else {
        onSearch(query.trim());
        scrollToResults();
      }
    }
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 479px)");
    const handler = (e) => setIsSmall(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (
        inputRef.current &&
        !inputRef.current.closest(".hero__search-wrap").contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    if (activeIdx >= 0 && listRef.current) {
      const item = listRef.current.children[activeIdx];
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  useEffect(() => {
    const timer = setInterval(() => {
      setSubtitleIdx((i) => (i + 1) % SUBTITLES.length);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  useImperativeHandle(ref, () => ({
    openVideo: () => setVideoOpen(true),
  }));

  useEffect(() => {
    if (!newsOpen && !videoOpen) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setNewsOpen(false);
        setVideoOpen(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [newsOpen, videoOpen]);

  return (
    <section className="hero" aria-label="Hero">
      <div className="hero__bg">
        <div className="hero__orb hero__orb--1" />
        <div className="hero__orb hero__orb--2" />
        <div className="hero__orb hero__orb--3" />
        <div className="hero__grid" />
      </div>

      <div className="hero__content container">
        <div className="hero__text animate-fade-up">
          <span className="hero__eyebrow">
            Rigorous applied math education.
          </span>
          <h1 className="display-xl hero__title">
            Deeper roots
            <br />
            <em>Higher branches</em>
          </h1>
          <div className="hero__subtitle-wrap" aria-live="polite">
            {SUBTITLES.map((s, i) => (
              <p
                key={i}
                className={`hero__subtitle hero__subtitle--${s.type}${i === subtitleIdx ? " hero__subtitle--active" : ""}`}
                aria-hidden={i !== subtitleIdx}
              >
                {s.text}
              </p>
            ))}
          </div>
          <button
            type="button"
            className="hero__how-it-works"
            onClick={() => setVideoOpen(true)}
            aria-haspopup="dialog"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
            How does this site work?
          </button>
        </div>

        <div className="hero__search-container animate-fade-up animate-fade-up-delay-1">
          <p className="hero__search-label">What do you want to learn today?</p>
          <form
            className="hero__search-wrap"
            onSubmit={handleSubmit}
            role="search"
          >
            <div className="hero__search-inner">
              <svg
                className="hero__search-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                className="hero__input"
                placeholder={
                  isSmall
                    ? "Search topics…"
                    : "Search courses, topics, or concepts…"
                }
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsOpen(true)}
                role="combobox"
                aria-label="Search courses"
                aria-autocomplete="list"
                aria-controls="hero-suggestions"
                aria-expanded={isOpen}
                autoComplete="off"
              />
              <button
                type="submit"
                className="hero__search-btn"
                aria-label="Search"
              >
                Search
              </button>
            </div>

            {isOpen && suggestions.length > 0 && (
              <ul
                id="hero-suggestions"
                className="hero__suggestions"
                ref={listRef}
                role="listbox"
                aria-label="Search suggestions"
              >
                {suggestions.map((tag, i) => (
                  <li
                    key={tag}
                    role="option"
                    aria-selected={i === activeIdx}
                    className={`hero__suggestion${i === activeIdx ? " hero__suggestion--active" : ""}`}
                    onMouseDown={() => selectTag(tag)}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                      <line x1="7" y1="7" x2="7.01" y2="7" />
                    </svg>
                    {tag}
                  </li>
                ))}
              </ul>
            )}
          </form>
        </div>

        <div className="hero__bottom animate-fade-up animate-fade-up-delay-2">
          <div className="hero__news">
            {/* Full list — viewport height ≥ 1100px */}
            <div className="hero__news-full" aria-label="Recent updates">
              <p className="hero__news-label">Recent updates</p>
              <ul className="hero__news-list">
                {NEWS_ITEMS.slice(0, PREVIEW_COUNT).map((item, i) => (
                  <li key={i} className="hero__news-item">
                    <time className="hero__news-date" dateTime={item.date}>
                      {formatDate(item.date)}
                    </time>
                    <p className="hero__news-text">{item.text}</p>
                  </li>
                ))}
              </ul>
              <button
                className="hero__news-more"
                onClick={() => setNewsOpen(true)}
                aria-haspopup="dialog"
              >
                All updates
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Compact link — viewport height < 1100px */}
            <button
              className="hero__news-compact"
              onClick={() => setNewsOpen(true)}
              aria-haspopup="dialog"
            >
              <span className="hero__news-compact-dot" aria-hidden="true" />
              Recent updates
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="hero__scroll-indicator" aria-hidden="true">
        <span />
      </div>

      {newsOpen && (
        <div
          className="hero__news-overlay"
          onClick={() => setNewsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="All updates"
        >
          <div
            className="hero__news-modal"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hero__news-modal-header">
              <h2 className="hero__news-modal-title">All Updates</h2>
              <button
                className="hero__news-modal-close"
                onClick={() => setNewsOpen(false)}
                aria-label="Close updates"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ul className="hero__news-modal-list">
              {NEWS_ITEMS.map((item, i) => (
                <li key={i} className="hero__news-modal-item">
                  <time className="hero__news-modal-date" dateTime={item.date}>
                    {formatDate(item.date)}
                  </time>
                  <p className="hero__news-modal-text">{item.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {videoOpen && (
        <div
          className="hero__news-overlay"
          onClick={() => setVideoOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="How this site works"
        >
          <div
            className="hero__video-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hero__news-modal-header">
              <h2 className="hero__news-modal-title">How This Site Works</h2>
              <button
                className="hero__news-modal-close"
                onClick={() => setVideoOpen(false)}
                aria-label="Close video"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="hero__video-frame">
              <iframe
                src="https://player.vimeo.com/video/1209632366?h=ea83935b69&autoplay=1&muted=1"
                title="How this site works"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
});

export default Hero;
